<template>
  <div class="space-y-6">
    <!-- 受众定位 -->
    <div class="bg-white rounded-lg border border-gray-100 p-4">
      <div class="flex items-center space-x-2 mb-4">
        <i class="el-icon-s-custom"></i>
        <span class="text-sm font-medium text-gray-700">受众定位</span>
      </div>
      <div class="grid grid-cols-2 gap-6">
        <div class="space-y-4">
          <div>
            <div class="text-xs text-gray-500 mb-2">地理位置(地区/城市)</div>
            <div class="text-sm text-gray-800">{{ getLocations() }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-500 mb-2">受众群体</div>
            <div class="text-sm text-gray-800">{{ getAudienceName() }}</div>
          </div>
        </div>
        <div class="space-y-4">
          <div>
            <div class="text-xs text-gray-500 mb-2">语言</div>
            <div class="text-sm text-gray-800">{{ getLanguages() }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-500 mb-2">投放渠道</div>
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-700">{{ getChannelSetting() }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-6">
        <div class="space-y-4">
          <div class="mt-4">
            <div class="text-xs text-gray-500 mb-2">广告组出价</div>
            <div v-if="showAdsetBid">
              <AdsetBid
                ref="adsetBid"
                :notShowTips="true"
                :currentAccount="currentAccount"
                :value="data"
                @input="(v) => setAdsetBid(v, index)"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="mt-4 pt-4 border-t border-gray-100">
        <div class="text-xs text-gray-500 mb-2">优化型定位</div>
        <div class="space-y-2">
          <div class="text-sm text-gray-800">
            {{ optimizePositionData.optimizedTargetingEnabled ? '启用' : '停用' }}
          </div>
          <div
            v-if="optimizePositionData.excludeDemographicExpansion"
            class="flex items-start space-x-2 ml-2"
          >
            <span class="text-sm text-blue-600">✓</span>
            <span class="text-sm text-gray-700">仅向符合我的年龄和性别要求的用户展示广告</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 广告素材预览 -->
    <div class="bg-white rounded-lg border border-gray-100 p-4">
      <div class="flex items-center space-x-2 mb-4">
        <i class="el-icon-picture"></i>
        <span class="text-sm font-medium text-gray-700">广告</span>
        <span class="text-xs text-gray-400">({{ adsCount }} items)</span>
      </div>
      <div class="space-y-4">
        <div
          v-for="(ad, index) in data.ads || []"
          :key="index"
          class="border border-gray-200 rounded-lg p-4"
        >
          <div class="flex items-center space-x-2 mb-3 justify-between">
            <div class="flex items-center flex-1">
              <span
                class="text-xs px-2 py-0.5 rounded"
                :class="getAdTagClass(index)"
                >广告{{ index + 1 }}</span
              >
              <div class="ml-2 flex-1">
                <el-form
                  :ref="`adNameForm_${index}`"
                  :model="ad"
                  :rules="getAdNameRules(index)"
                  class="ad-name-form"
                  @submit.native.prevent
                >
                  <el-form-item
                    prop="name"
                    class="mb-0"
                  >
                    <el-input
                      :value="ad.name"
                      size="small"
                      placeholder="请输入广告名称"
                      @input="(val) => handleAdNameChange(index, val)"
                      @blur="() => validateAdName(index)"
                    ></el-input>
                  </el-form-item>
                </el-form>
              </div>
            </div>
            <div>
              <div class="flex items-center space-x-4">
                <el-switch
                  :value="ad.status === 'ENABLED'"
                  active-color="#8b5cf6"
                  inactive-color="#d1d5db"
                  @change="(val) => handleAdStatusChange(index, val)"
                />
                <el-button
                  type="text"
                  size="mini"
                  class="text-red-500 hover:text-red-700"
                  :disabled="!canDeleteAd"
                  @click="handleDeleteAd(index)"
                >
                  <i class="el-icon-delete"></i>
                </el-button>
              </div>
            </div>
          </div>
          <!-- 根据广告类型显示视频或图片 -->
          <div class="flex space-x-3 mb-3 flex-wrap">
            <!-- 视频类型广告 -->
            <template v-if="isVideoAd(ad)">
              <div
                v-for="(video, vIndex) in ad.videoAssets || []"
                :key="'video-' + vIndex"
                class="video-preview-item rounded-lg overflow-hidden relative bg-gray-100"
              >
                <img
                  v-if="video.thumbnail"
                  :src="video.thumbnail"
                  :alt="video.name || video.title"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center bg-gray-200"
                >
                  <i class="el-icon-video-camera text-2xl text-gray-400"></i>
                </div>
                <div
                  v-if="video.videoId"
                  class="absolute inset-0 flex items-center justify-center"
                >
                  <div class="w-8 h-8 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <i class="el-icon-caret-right text-white"></i>
                  </div>
                </div>
              </div>
            </template>
            <!-- 图片类型广告 -->
            <template v-else-if="hasImages(ad)">
              <div
                v-for="(image, iIndex) in getAdImages(ad)"
                :key="'image-' + iIndex"
                class="w-16 h-16 rounded-lg overflow-hidden"
              >
                <img
                  v-if="image.url"
                  :src="image.url"
                  :alt="image.name"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center bg-gray-200"
                >
                  <i class="el-icon-picture text-xl text-gray-400"></i>
                </div>
              </div>
            </template>
          </div>
          <div class="text-xs text-gray-500 mb-1">目标网址</div>
          <div class="text-xs text-blue-600 truncate">
            {{ promotionLink || ad.finalUrl || 'https://example.com/...' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdsetBid from '@/views/google/create/components/audience/AdsetBid/index.vue';

// 投放渠道类型映射
const CHANNEL_TYPE_MAP = {
  ALL_CHANNELS: '所有渠道',
  YOUTUBE_ONLY: '仅 YouTube',
  DISPLAY_ONLY: '仅展示广告网络',
  CUSTOM: '自定义渠道',
};

// 渠道设置字段映射
const CHANNEL_FIELD_MAP = {
  discover: 'Discover',
  display: '展示广告',
  gmail: 'Gmail',
  youtubeInFeed: 'YouTube 信息流',
  youtubeInStream: 'YouTube 插播',
  youtubeShorts: 'YouTube Shorts',
};

export default {
  name: 'AdGroupDetail',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    currency: {
      type: String,
      default: 'USD',
    },
    adGroupIndex: {
      type: Number,
      default: 0,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
    currentAccount: {
      type: Object,
      default: () => ({}),
    },
    enumConstants: {
      type: Object,
      default: () => ({}),
    },
    promotionLink: {
      type: String,
      default: '',
    },
  },
  data() {
    // 广告名称重复校验器
    const validateAdNameUnique = (rule, value, callback) => {
      if (!value || value.trim() === '') {
        callback(new Error('广告名称不能为空'));
        return;
      }
      // 获取当前广告索引
      const currentIndex = rule.adIndex;
      const ads = this.data.ads || [];
      // 检查同一广告组下是否有重复的广告名称
      const duplicateIndex = ads.findIndex((ad, index) => {
        return index !== currentIndex && ad.name === value.trim();
      });
      if (duplicateIndex !== -1) {
        callback(new Error('同一广告组下广告名称不能重复'));
      } else {
        callback();
      }
    };

    return {
      localName: '',
      localStatus: true,
      optimizePositionData: {
        optimizedTargetingEnabled: true,
        excludeDemographicExpansion: false,
      },
      adGroupFormData: {
        name: '',
      },
      adGroupRules: {
        name: [
          { required: true, message: '广告组名称不能为空', trigger: ['blur', 'change'] },
          { max: 375, message: '广告组名称长度不能超过375字符', trigger: ['blur', 'change'] },
        ],
      },
      validateAdNameUnique,
    };
  },
  components: {
    AdsetBid,
  },
  computed: {
    adsCount() {
      return this.data.ads ? this.data.ads.length : 0;
    },
    canDeleteAd() {
      return this.data.ads && this.data.ads.length > 1;
    },
    // 广告组出价区域是否展示：系列设置了有效出价值（TARGET_CPA/TARGET_ROAS）时显示
    showAdsetBid() {
      return ['TARGET_CPA', 'TARGET_ROAS'].includes(this.data.campaignBidStrategy);
    },
  },
  watch: {
    'data.name': {
      handler(val) {
        this.localName = val || '';
        this.adGroupFormData.name = val || '';
      },
      immediate: true,
    },
    'data.status': {
      handler(val) {
        this.localStatus = val === 'ENABLED';
      },
      immediate: true,
    },
    data: {
      handler(val) {
        // 同步优化型定位数据
        this.optimizePositionData = {
          optimizedTargetingEnabled: val.optimizedTargetingEnabled !== undefined ? val.optimizedTargetingEnabled : true,
          excludeDemographicExpansion: val.excludeDemographicExpansion === true,
        };
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    // 获取地理位置 - 从 groupCriterion.target.locations 获取
    getLocations() {
      const target = this.data.groupCriterion?.target;
      if (target && target.locations && target.locations.length > 0) {
        return target.locations.map((l) => l.name || l.code || l).join(', ');
      }
      // 兼容旧数据结构
      if (this.data.locations && this.data.locations.length > 0) {
        return this.data.locations.map((l) => l.name || l).join(', ');
      }
      return '未设置';
    },

    // 获取语言 - 从 groupCriterion.target.languages 获取，根据 enumConstants 查找中文名
    getLanguages() {
      const target = this.data.groupCriterion?.target;
      const languageIds = target?.languages || this.data.languages || [];

      if (!languageIds || languageIds.length === 0) {
        return '未设置';
      }

      const languageList = this.enumConstants?.languages || [];

      const languageNames = languageIds.map((langId) => {
        // 如果是对象，取id
        const id = typeof langId === 'object' ? langId.id : langId;
        const found = languageList.find((lang) => lang.id === id || lang.value === id);
        // 优先使用 nameZhcn 字段
        return found ? found.nameZhcn || found.name || found.label || found.text : `语言ID: ${id}`;
      });

      return languageNames.join(', ');
    },

    // 获取投放渠道设置
    getChannelSetting() {
      const channelSetting = this.data.channelSetting;
      if (!channelSetting) {
        return '未设置';
      }

      // 如果有渠道类型
      if (channelSetting.channelType) {
        const typeName = CHANNEL_TYPE_MAP[channelSetting.channelType] || channelSetting.channelType;

        // 如果是所有渠道，直接返回
        if (channelSetting.channelType === 'ALL_CHANNELS') {
          return '✓ ' + typeName;
        }
      }

      // 收集启用的渠道
      const enabledChannels = [];
      Object.keys(CHANNEL_FIELD_MAP).forEach((key) => {
        if (channelSetting[key]) {
          enabledChannels.push(CHANNEL_FIELD_MAP[key]);
        }
      });

      if (enabledChannels.length > 0) {
        return '✓ ' + enabledChannels.join(', ');
      }

      return CHANNEL_TYPE_MAP[channelSetting.channelType] || '未设置';
    },

    // 获取受众群组
    getAudienceName() {
      const audience = this.data.groupCriterion?.audience;
      return audience?.name || '未选择';
    },

    // 判断是否为视频广告
    isVideoAd(ad) {
      const videoTypes = ['DEMAND_GEN_VIDEO_RESPONSIVE_AD', 'VIDEO_RESPONSIVE_AD', 'VIDEO_AD', 'IN_STREAM_VIDEO_AD'];
      return videoTypes.includes(ad.type) && ad.videoAssets && ad.videoAssets.length > 0;
    },

    // 判断是否有图片
    hasImages(ad) {
      return (
        (ad.multiImages && ad.multiImages.length > 0) ||
        (ad.logoImages && ad.logoImages.length > 0) ||
        (ad.imageAssets && ad.imageAssets.length > 0)
      );
    },

    // 获取广告图片
    getAdImages(ad) {
      // 优先使用 multiImages
      if (ad.multiImages && ad.multiImages.length > 0) {
        return ad.multiImages;
      }
      return [];
    },

    getAdTagClass(index) {
      const classes = [
        'bg-purple-100 text-purple-700',
        'bg-blue-100 text-blue-700',
        'bg-green-100 text-green-700',
        'bg-orange-100 text-orange-700',
      ];
      return classes[index % classes.length];
    },
    getAdColors(index) {
      const colorSets = [
        ['bg-yellow-200', 'bg-green-200', 'bg-pink-200', 'bg-purple-200'],
        ['bg-blue-200', 'bg-indigo-200', 'bg-cyan-200', 'bg-teal-200'],
        ['bg-orange-200', 'bg-red-200', 'bg-rose-200', 'bg-amber-200'],
      ];
      return colorSets[index % colorSets.length];
    },
    handleNameChange(value) {
      this.adGroupFormData.name = value;
      this.$emit('update:name', this.adGroupIndex, value);
    },

    handleAdNameChange(adIndex, value) {
      this.$emit('update:ad-name', this.adGroupIndex, adIndex, value);
    },
    // 获取广告名称校验规则（带重复校验）
    getAdNameRules(adIndex) {
      return {
        name: [
          { required: true, message: '广告名称不能为空', trigger: ['blur', 'change'] },
          { max: 375, message: '广告名称长度不能超过375字符', trigger: ['blur', 'change'] },
          { validator: this.validateAdNameUnique, adIndex: adIndex, trigger: ['blur', 'change'] },
        ],
      };
    },
    // 手动触发广告名称校验
    validateAdName(adIndex) {
      const nameFormRef = `adNameForm_${adIndex}`;
      const nameFormArr = this.$refs[nameFormRef];
      const nameForm = Array.isArray(nameFormArr) ? nameFormArr[0] : nameFormArr;
      if (nameForm && nameForm.validate) {
        nameForm.validate().catch(() => {});
      }
    },
    handleAdStatusChange(adIndex, value) {
      this.$emit('update:ad-status', this.adGroupIndex, adIndex, value ? 'ENABLED' : 'PAUSED');
    },
    handleDeleteAd(adIndex) {
      this.$emit('delete:ad', this.adGroupIndex, adIndex);
    },
    handleOptimizePositionChange(val) {
      this.optimizePositionData = val;
      this.$emit('update:optimize-position', this.adGroupIndex, val);
    },
    selectAd(adIndex) {
      this.$emit('ad-selected', this.adGroupIndex, adIndex);
    },
    // 对外提供的验证方法
    validate() {
      const promises = [];
      // 校验广告组出价
      if (this.$refs.adsetBid && this.$refs.adsetBid.validate) {
        const bidValid = this.$refs.adsetBid.validate();
        if (!bidValid) {
          promises.push(Promise.reject(new Error('广告组出价校验失败')));
        }
      }
      // 校验所有广告名称表单
      const ads = this.data.ads || [];
      for (let i = 0; i < ads.length; i++) {
        const nameFormRef = `adNameForm_${i}`;
        const nameFormArr = this.$refs[nameFormRef];
        const nameForm = Array.isArray(nameFormArr) ? nameFormArr[0] : nameFormArr;
        if (nameForm && nameForm.validate) {
          promises.push(nameForm.validate());
        }
      }
      if (promises.length === 0) {
        return Promise.resolve(true);
      }
      return Promise.all(promises);
    },
    // 设置广告组出价
    setAdsetBid(val, index) {
      this.$emit('update:bid', this.adGroupIndex, val);
    },
  },
};
</script>

<style scoped>
.max-w-xs {
  max-width: 200px;
}

.video-preview-item {
  width: 120px;
  height: 90px;
}
</style>
